import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarCheck, CalendarDays, HeartHandshake, Quote, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useLang } from "@/i18n";
import { http, errText } from "@/lib/api";
import { Num, Reveal, SectionHead } from "./Primitives";

const TOOL_LINKS = [["/kundali", Sparkles], ["/milan", HeartHandshake], ["/panchang", CalendarDays], ["/book", CalendarCheck]];

export const Tools = () => {
  const { t } = useLang();
  return (
    <section id="tools" data-testid="tools-section" className="mx-auto max-w-[1440px] px-5 sm:px-8 py-24 lg:py-32">
      <SectionHead eyebrow={t.tools.eyebrow} title={t.tools.title} desc={t.tools.desc} testid="tools" />
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {t.tools.items.map((it, i) => {
          const [to, Icon] = TOOL_LINKS[i];
          return (
            <Reveal key={to} delay={i * 0.08}>
              <Link to={to} data-testid={`tool-link-${to.slice(1)}`} className="card group h-full flex flex-col justify-between p-7 min-h-[230px]">
                <div className="flex justify-between"><Icon className="w-7 h-7 text-[color:var(--saffron)]" strokeWidth={1.3} /><Num n={i + 1} /></div>
                <div>
                  <h3 className="display text-3xl">{it.title}</h3>
                  <p className="mt-2 text-sm text-[color:var(--muted)]">{it.text}</p>
                  <ArrowUpRight className="mt-4 w-5 h-5 text-[color:var(--gold)] transition-transform duration-500 group-hover:rotate-45" />
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const StoryForm = ({ t }) => {
  const [f, setF] = useState({ name: "", location: "", text: "" });
  const [st, setSt] = useState({ busy: false, msg: "", ok: false });
  const submit = async (e) => {
    e.preventDefault();
    setSt({ busy: true, msg: "", ok: false });
    try { await http.post("/stories", f); setSt({ busy: false, msg: t.stories.sent, ok: true }); setF({ name: "", location: "", text: "" }); }
    catch (ex) { setSt({ busy: false, msg: errText(ex, t.stories.error), ok: false }); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="story-form">
      <input data-testid="story-name" required minLength={2} className="field" placeholder={t.stories.name} value={f.name} onChange={set("name")} />
      <input data-testid="story-location" required minLength={2} className="field" placeholder={t.stories.location} value={f.location} onChange={set("location")} />
      <textarea data-testid="story-text" required minLength={20} maxLength={800} rows={5} className="field" placeholder={t.stories.text} value={f.text} onChange={set("text")} />
      {st.msg && <p data-testid="story-form-msg" className={`text-sm ${st.ok ? "text-emerald-700" : "text-red-700"}`}>{st.msg}</p>}
      <button data-testid="story-submit" disabled={st.busy} className="pill-solid px-6 py-3 text-sm disabled:opacity-60">{st.busy ? t.stories.sending : t.stories.submit}</button>
    </form>
  );
};

export const Stories = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    http.get("/stories").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  }, []);
  return (
    <section id="stories" data-testid="stories-section" className="mx-auto max-w-[1440px] px-5 sm:px-8 py-24 lg:py-32">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
        <SectionHead eyebrow={t.stories.eyebrow} title={t.stories.title} testid="stories" />
        <Dialog>
          <DialogTrigger data-testid="story-open-btn" className="pill-solid px-7 py-3.5 text-sm self-start md:self-auto">{t.stories.share}</DialogTrigger>
          <DialogContent className="bg-[color:var(--bg)] border-[color:var(--line)] text-[color:var(--ink)] rounded-3xl">
            <DialogTitle className="display text-3xl font-medium">{t.stories.formTitle}</DialogTitle>
            <StoryForm t={t} />
          </DialogContent>
        </Dialog>
      </div>
      {rows.length === 0 ? (
        <p data-testid="stories-empty" className="mt-12 text-[color:var(--muted)]">{t.stories.empty}</p>
      ) : (
        <div className="mt-14 columns-1 md:columns-2 lg:columns-3 gap-5">
          {rows.map((s, i) => (
            <figure key={s.id} className="card p-8 mb-5 break-inside-avoid" data-testid={`story-card-${i}`}>
              <Quote className="w-6 h-6 text-[color:var(--saffron)]" />
              <blockquote className="mt-4 display text-xl leading-snug">{s.text}</blockquote>
              <figcaption className="mt-6 text-sm"><b>{s.name}</b> <span className="text-[color:var(--muted)]">· {s.location}</span></figcaption>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
};
